import { useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t, tn } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { dateTime, elapsed, minutesSince } from '../../lib/format.ts';
import { useUi } from '../../lib/ui-state.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';

interface Printer {
  id: string;
  name: string;
  host: string;
  port: number;
  paper: '80' | '58';
  code_page: 'cp858' | 'ascii';
  cut: boolean;
  drawer: boolean;
  agent: string | null;
  active: boolean;
  queued: number;
  failed: number;
  last_error: string | null;
  last_printed_at: string | null;
}
interface Agent {
  id: string;
  name: string;
  last_seen_at: string | null;
  version: string | null;
  online: boolean;
}
interface Overview {
  printers: Printer[];
  agents: Agent[];
  locations?: { id: string; name: string; code: string }[];
  registers: { id: string; name: string; printer: string | null; location?: string }[];
  stations: { id: string; name: string; kind: 'kitchen' | 'bar'; printer: string | null; printers?: Record<string, string | null> }[];
}
type Target = { register: string } | { station: string; location?: string };
export interface Job {
  id: string;
  printer: string;
  printer_name: string;
  kind: 'receipt' | 'bill' | 'kitchen' | 'cancel' | 'drawer' | 'test';
  title: string;
  state: 'queued' | 'done' | 'failed' | 'cancelled';
  attempts: number;
  error: string | null;
  created_at: string;
  done_at: string | null;
  created_by_name: string | null;
}

/** Receipt and kitchen printers on the network: where each print goes and whether it came out. */
export function Printers() {
  const { data, reload } = useAction<Overview>('printing.overview');
  const { data: jobs, reload: reloadJobs } = useAction<Job[]>('printing.jobs');
  const { run } = useSave();
  const [editing, setEditing] = useState<Printer | 'new' | null>(null);
  const [addingAgent, setAddingAgent] = useState(false);
  const [removing, setRemoving] = useState<Agent | null>(null);
  const refresh = () => Promise.all([reload(), reloadJobs()]);

  const test = (p: Printer) =>
    void run(async () => {
      await action('printing.test', { printer: p.id });
      // Give the printer a moment, then show whether it came out.
      setTimeout(() => void refresh(), 1500);
    }, t('printers.test_sent', { name: p.name }));

  const assign = (target: Target, printer: string) =>
    void run(async () => {
      await action('printing.assign', { ...target, printer: printer || null });
      await reload();
    }, t('printers.saved_where'));

  if (!data) {
    return (
      <ManageLayout title={t('manage.printers')} subtitle={t('printers.subtitle')}>
        <Skeleton h={420} r="var(--radius-lg)" />
      </ManageLayout>
    );
  }
  const agentName = (id: string | null) => data.agents.find((a) => a.id === id)?.name;

  return (
    <ManageLayout
      title={t('manage.printers')}
      subtitle={t('printers.subtitle')}
      actions={
        <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
          {t('printers.add')}
        </Button>
      }
    >
      <div className="printers">
        <section className="list-card" aria-labelledby="printers-title">
          <div className="list-card-head">
            <div>
              <h2 id="printers-title">{t('printers.list')}</h2>
              <p className="muted">{t('printers.list_hint')}</p>
            </div>
          </div>
          {data.printers.length === 0 ? (
            <EmptyState
              icon="printer"
              title={t('printers.empty')}
              body={t('printers.empty_body')}
              action={
                <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
                  {t('printers.add')}
                </Button>
              }
            />
          ) : (
            data.printers.map((p) => (
              <div key={p.id} className={`list-row printer-row${p.active ? '' : ' archived'}`}>
                <span className="list-icon" aria-hidden="true">
                  <Icon name="printer" size={20} />
                </span>
                <span className="list-main">
                  <span className="list-title">{p.name}</span>
                  <span className="list-sub">
                    <span className="mono">
                      {p.host}:{p.port}
                    </span>
                    {' · '}
                    {t(p.paper === '58' ? 'printers.paper_58' : 'printers.paper_80')}
                    {p.agent ? ` · ${t('printers.via', { name: agentName(p.agent) ?? '' })}` : ''}
                    {p.drawer ? ` · ${t('printers.has_drawer')}` : ''}
                  </span>
                  {p.last_error && (p.failed || p.queued) ? <span className="list-sub text-danger">{p.last_error}</span> : null}
                </span>
                <span className="row-actions">
                  <PrinterState p={p} />
                  <Button size="sm" icon="printer" onClick={() => test(p)} disabled={!p.active}>
                    {t('printers.test')}
                  </Button>
                  <IconButton icon="edit" label={t('printers.edit', { name: p.name })} onClick={() => setEditing(p)} />
                </span>
              </div>
            ))
          )}
        </section>

        {data.printers.length ? <Where data={data} onAssign={assign} /> : null}

        <section className="list-card" aria-labelledby="agents-title">
          <div className="list-card-head">
            <div>
              <h2 id="agents-title">{t('printers.agents')}</h2>
              <p className="muted">{t('printers.agents_hint')}</p>
            </div>
            <Button icon="plus" onClick={() => setAddingAgent(true)}>
              {t('printers.add_agent')}
            </Button>
          </div>
          {data.agents.length === 0 ? (
            <p className="list-empty muted">{t('printers.no_agents')}</p>
          ) : (
            data.agents.map((a) => (
              <div key={a.id} className="list-row">
                <span className="list-icon" aria-hidden="true">
                  <Icon name="send" size={20} />
                </span>
                <span className="list-main">
                  <span className="list-title">{a.name}</span>
                  <span className="list-sub">
                    {!a.last_seen_at
                      ? t('printers.agent_never')
                      : minutesSince(a.last_seen_at) < 1
                        ? t('printers.agent_seen_now')
                        : t('printers.agent_seen', { ago: elapsed(a.last_seen_at) })}
                    {a.version ? ` · ${t('printers.agent_version', { v: a.version })}` : ''}
                  </span>
                </span>
                {a.online ? (
                  <StatusBadge tone="ok" icon="dot" label={t('printers.agent_online')} />
                ) : (
                  <StatusBadge tone={a.last_seen_at ? 'danger' : 'neutral'} icon="wifiOff" label={t('printers.agent_offline')} />
                )}
                <IconButton icon="trash" label={t('printers.remove_agent', { name: a.name })} onClick={() => setRemoving(a)} />
              </div>
            ))
          )}
        </section>

        <JobLog jobs={jobs} onChange={refresh} />
      </div>

      {editing ? (
        <PrinterDialog
          printer={editing === 'new' ? null : editing}
          agents={data.agents}
          taken={data.printers.filter((p) => editing === 'new' || p.id !== editing.id).map((p) => p.name)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      ) : null}
      {addingAgent ? (
        <AgentDialog
          taken={data.agents.map((a) => a.name)}
          onClose={() => {
            setAddingAgent(false);
            void reload();
          }}
        />
      ) : null}
      {removing ? (
        <Confirm
          title={t('printers.remove_agent', { name: removing.name })}
          body={t('printers.remove_agent_body')}
          confirm={t('printers.remove')}
          danger
          onClose={() => setRemoving(null)}
          onConfirm={() =>
            void run(async () => {
              await action('printing.remove_agent', { agent: removing.id });
              setRemoving(null);
              await reload();
            }, t('printers.agent_removed', { name: removing.name }))
          }
        />
      ) : null}
    </ManageLayout>
  );
}

function PrinterState({ p }: { p: Printer }) {
  if (!p.active) return <StatusBadge tone="neutral" icon="pause" label={t('printers.off')} />;
  if (p.failed) return <StatusBadge tone="danger" icon="alert" label={tn('printers.not_printing', p.failed)} />;
  if (p.queued && p.last_error) return <StatusBadge tone="warn" icon="clock" label={tn('printers.retrying', p.queued)} pulse />;
  if (p.queued) return <StatusBadge tone="info" icon="clock" label={tn('printers.waiting', p.queued)} />;
  if (p.last_printed_at) {
    const label = minutesSince(p.last_printed_at) < 1 ? t('printers.printed_just_now') : t('printers.printed_ago', { ago: elapsed(p.last_printed_at) });
    return <StatusBadge tone="ok" icon="check" label={label} />;
  }
  return <StatusBadge tone="neutral" icon="circle" label={t('printers.not_used')} />;
}

/**
 * Which printer each register and station uses. With several locations, each kitchen and bar
 * has a printer per location: an order at Blloku never prints in the Boulevard kitchen.
 */
function Where({ data, onAssign }: { data: Overview; onAssign: (target: Target, printer: string) => void }) {
  const places = data.locations ?? [];
  if (places.length > 1) {
    return (
      <section className="list-card" aria-labelledby="where-title">
        <div className="list-card-head">
          <div>
            <h2 id="where-title">{t('printers.where')}</h2>
            <p className="muted">{t('printers.where_hint_places')}</p>
          </div>
        </div>
        {places.map((l) => (
          <div key={l.id} role="group" aria-label={l.name}>
            <h3 className="where-place">
              <span className="place-badge" aria-hidden="true">
                {l.code}
              </span>
              {l.name}
            </h3>
            {data.registers
              .filter((r) => r.location === l.id)
              .map((r) => (
                <WhereRow key={r.id} icon="register" name={r.name} sub={t('printers.receipts_of')} value={r.printer} none={t('printers.browser_print')} printers={data.printers} onChange={(v) => onAssign({ register: r.id }, v)} />
              ))}
            {data.stations.map((s) => (
              <WhereRow
                key={s.id}
                icon={s.kind === 'bar' ? 'bag' : 'chef'}
                name={s.name}
                sub={t('printers.tickets_of')}
                value={s.printers?.[l.id] ?? null}
                none={t('printers.screen_only')}
                printers={data.printers}
                label={t('printers.printer_at', { name: s.name, place: l.name })}
                onChange={(v) => onAssign({ station: s.id, location: l.id }, v)}
              />
            ))}
          </div>
        ))}
      </section>
    );
  }
  return (
    <section className="list-card" aria-labelledby="where-title">
      <div className="list-card-head">
        <div>
          <h2 id="where-title">{t('printers.where')}</h2>
          <p className="muted">{t('printers.where_hint')}</p>
        </div>
      </div>
      {data.registers.map((r) => (
        <label key={r.id} className="list-row where-row">
          <span className="list-icon" aria-hidden="true">
            <Icon name="register" size={20} />
          </span>
          <span className="list-main">
            <span className="list-title">{r.name}</span>
            <span className="list-sub">{t('printers.receipts_of')}</span>
          </span>
          <select className="input" value={r.printer ?? ''} onChange={(e) => onAssign({ register: r.id }, e.target.value)} aria-label={t('printers.printer_for', { name: r.name })}>
            <option value="">{t('printers.browser_print')}</option>
            {data.printers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      ))}
      {data.stations.map((s) => (
        <label key={s.id} className="list-row where-row">
          <span className="list-icon" aria-hidden="true">
            <Icon name={s.kind === 'bar' ? 'bag' : 'chef'} size={20} />
          </span>
          <span className="list-main">
            <span className="list-title">{s.name}</span>
            <span className="list-sub">{t('printers.tickets_of')}</span>
          </span>
          <select className="input" value={s.printer ?? ''} onChange={(e) => onAssign({ station: s.id }, e.target.value)} aria-label={t('printers.printer_for', { name: s.name })}>
            <option value="">{t('printers.screen_only')}</option>
            {data.printers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      ))}
    </section>
  );
}

function WhereRow({
  icon,
  name,
  sub,
  value,
  none,
  printers,
  label,
  onChange,
}: {
  icon: 'register' | 'bag' | 'chef';
  name: string;
  sub: string;
  value: string | null;
  none: string;
  printers: Printer[];
  label?: string;
  onChange: (printer: string) => void;
}) {
  return (
    <label className="list-row where-row">
      <span className="list-icon" aria-hidden="true">
        <Icon name={icon} size={20} />
      </span>
      <span className="list-main">
        <span className="list-title">{name}</span>
        <span className="list-sub">{sub}</span>
      </span>
      <select className="input" value={value ?? ''} onChange={(e) => onChange(e.target.value)} aria-label={label ?? t('printers.printer_for', { name })}>
        <option value="">{none}</option>
        {printers.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  );
}

const KIND_ICON = { receipt: 'receipt', bill: 'note', kitchen: 'chef', cancel: 'x', drawer: 'cash', test: 'printer' } as const;

/** The last prints, newest first: what came out, what is waiting and why. */
export function JobLog({ jobs, onChange }: { jobs: Job[] | null; onChange: () => Promise<unknown> }) {
  const { run } = useSave();
  const act = (name: 'printing.retry' | 'printing.cancel', job: Job) =>
    void run(async () => {
      await action(name, { job: job.id });
      setTimeout(() => void onChange(), 1200);
      await onChange();
    }, name === 'printing.retry' ? t('printers.retried') : t('printers.stopped'));
  return (
    <section className="list-card" aria-labelledby="jobs-title">
      <div className="list-card-head">
        <div>
          <h2 id="jobs-title">{t('printers.jobs')}</h2>
          <p className="muted">{t('printers.jobs_hint')}</p>
        </div>
        <IconButton icon="undo" label={t('printers.refresh')} onClick={() => void onChange()} />
      </div>
      {!jobs ? (
        <Skeleton h={120} />
      ) : jobs.length === 0 ? (
        <p className="list-empty muted">{t('printers.no_jobs')}</p>
      ) : (
        jobs.slice(0, 30).map((j) => (
          <div key={j.id} className="list-row job-row">
            <span className="list-icon" aria-hidden="true">
              <Icon name={KIND_ICON[j.kind]} size={18} />
            </span>
            <span className="list-main">
              <span className="list-title">{j.title}</span>
              <span className="list-sub">
                {j.printer_name} · {dateTime(j.created_at)}
                {j.created_by_name ? ` · ${j.created_by_name}` : ''}
              </span>
              {j.error && j.state !== 'done' && j.state !== 'cancelled' ? <span className="list-sub text-danger">{j.error}</span> : null}
            </span>
            <span className="row-actions">
              <JobState j={j} />
              {j.state === 'failed' || (j.state === 'queued' && j.attempts > 0) ? (
                <>
                  <Button size="sm" icon="undo" onClick={() => act('printing.retry', j)}>
                    {t('printers.retry')}
                  </Button>
                  <IconButton icon="x" label={t('printers.stop', { title: j.title })} onClick={() => act('printing.cancel', j)} />
                </>
              ) : null}
            </span>
          </div>
        ))
      )}
    </section>
  );
}

function JobState({ j }: { j: Job }) {
  switch (j.state) {
    case 'done':
      return <StatusBadge tone="ok" icon="check" label={t('printers.job_done')} />;
    case 'failed':
      return <StatusBadge tone="danger" icon="alert" label={t('printers.job_failed')} />;
    case 'cancelled':
      return <StatusBadge tone="neutral" icon="x" label={t('printers.job_stopped')} />;
    default:
      return j.attempts > 0 ? (
        <StatusBadge tone="warn" icon="clock" label={t('printers.job_retrying')} pulse />
      ) : (
        <StatusBadge tone="info" icon="clock" label={t('printers.job_waiting')} />
      );
  }
}

function PrinterDialog({
  printer,
  agents,
  taken,
  onClose,
  onSaved,
}: {
  printer: Printer | null;
  agents: Agent[];
  taken: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { busy, run } = useSave();
  const { toast } = useUi();
  const [form, setForm] = useState({
    name: printer?.name ?? '',
    host: printer?.host ?? '',
    port: String(printer?.port ?? 9100),
    paper: printer?.paper ?? '80',
    code_page: printer?.code_page ?? 'cp858',
    cut: printer?.cut ?? true,
    drawer: printer?.drawer ?? false,
    agent: printer?.agent ?? '',
    active: printer?.active ?? true,
  });
  const [deleting, setDeleting] = useState(false);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const name = form.name.trim();
  const isTaken = taken.some((x) => x.toLowerCase() === name.toLowerCase());
  const port = Number(form.port);
  const portOk = Number.isInteger(port) && port > 0 && port < 65536;
  const hostOk = /^[A-Za-z0-9.\-:]+$/.test(form.host.trim());
  const ok = name && !isTaken && hostOk && portOk;

  const save = () => {
    if (!ok) return;
    void run(
      async () => {
        await action('printing.save_printer', {
          ...(printer ? { printer: printer.id } : {}),
          ...form,
          name,
          host: form.host.trim(),
          port,
          agent: form.agent || null,
        });
        onSaved();
      },
      printer ? t('menu.saved', { name }) : t('menu.added', { name }),
    );
  };

  return (
    <Dialog
      title={printer ? printer.name : t('printers.add')}
      onClose={onClose}
      footer={
        <>
          {printer ? (
            <Button variant="ghost" icon="trash" className="dialog-left" onClick={() => setDeleting(true)}>
              {t('printers.delete')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!ok}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        className="form-stack"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <Field label={t('printers.name')} hint={t('printers.name_hint')} error={isTaken ? t('printers.name_taken') : null}>
          <input className="input" value={form.name} maxLength={60} onChange={(e) => set('name', e.target.value)} data-autofocus />
        </Field>
        <div className="form-row">
          <Field label={t('printers.address')} hint={t('printers.address_hint')} error={form.host && !hostOk ? t('printers.address_bad') : null}>
            <input className="input mono" value={form.host} inputMode="url" autoCapitalize="off" spellCheck={false} placeholder="192.168.1.50" onChange={(e) => set('host', e.target.value)} />
          </Field>
          <Field label={t('printers.port')} error={!portOk ? t('printers.port_bad') : null}>
            <input className="input mono" value={form.port} inputMode="numeric" maxLength={5} onChange={(e) => set('port', e.target.value.replace(/\D/g, ''))} />
          </Field>
        </div>
        <fieldset className="option-group">
          <legend>{t('printers.paper')}</legend>
          <div className="segmented" role="radiogroup" aria-label={t('printers.paper')}>
            {(['80', '58'] as const).map((p) => (
              <button key={p} type="button" role="radio" aria-checked={form.paper === p} className={form.paper === p ? 'active' : ''} onClick={() => set('paper', p)}>
                {t(p === '58' ? 'printers.paper_58' : 'printers.paper_80')}
              </button>
            ))}
          </div>
        </fieldset>
        <Field label={t('printers.sent_by')} hint={form.agent ? t('printers.sent_by_agent_hint') : t('printers.sent_by_server_hint')}>
          <select className="input" value={form.agent} onChange={(e) => set('agent', e.target.value)}>
            <option value="">{t('printers.sent_by_server')}</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {t('printers.via', { name: a.name })}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('printers.letters')} hint={t('printers.letters_hint')}>
          <select className="input" value={form.code_page} onChange={(e) => set('code_page', e.target.value as 'cp858' | 'ascii')}>
            <option value="cp858">{t('printers.letters_full')}</option>
            <option value="ascii">{t('printers.letters_plain')}</option>
          </select>
        </Field>
        <label className="switch-row">
          <span>
            <span className="switch-title">{t('printers.cut')}</span>
            <span className="muted">{t('printers.cut_hint')}</span>
          </span>
          <Switch checked={form.cut} label={t('printers.cut')} onChange={(v) => set('cut', v)} />
        </label>
        <label className="switch-row">
          <span>
            <span className="switch-title">{t('printers.drawer')}</span>
            <span className="muted">{t('printers.drawer_hint')}</span>
          </span>
          <Switch checked={form.drawer} label={t('printers.drawer')} onChange={(v) => set('drawer', v)} />
        </label>
        <label className="switch-row">
          <span>
            <span className="switch-title">{t('printers.active')}</span>
            <span className="muted">{t('printers.active_hint')}</span>
          </span>
          <Switch checked={form.active} label={t('printers.active')} onChange={(v) => set('active', v)} />
        </label>
      </form>
      {deleting && printer ? (
        <Confirm
          title={t('printers.delete_title', { name: printer.name })}
          body={t('printers.delete_body')}
          confirm={t('printers.delete')}
          danger
          onClose={() => setDeleting(false)}
          onConfirm={() =>
            void run(async () => {
              await action('printing.delete_printer', { printer: printer.id });
              toast(t('printers.deleted', { name: printer.name }));
              onSaved();
            })
          }
        />
      ) : null}
    </Dialog>
  );
}

/** Adds a print agent and shows its key once, with how to start it. */
function AgentDialog({ taken, onClose }: { taken: string[]; onClose: () => void }) {
  const { busy, run } = useSave();
  const { toast } = useUi();
  const [name, setName] = useState('');
  const [made, setMade] = useState<{ name: string; key: string } | null>(null);
  const clean = name.trim();
  const isTaken = taken.some((x) => x.toLowerCase() === clean.toLowerCase());
  const command = made ? `node print-agent.mjs --url ${window.location.origin} --key ${made.key}` : '';

  const add = () => {
    if (!clean || isTaken) return;
    void run(async () => setMade(await action<{ name: string; key: string }>('printing.add_agent', { name: clean })));
  };
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(t('printers.copied'));
    } catch {
      toast(t('printers.copy_failed'));
    }
  };

  return (
    <Dialog
      title={made ? t('printers.agent_added', { name: made.name }) : t('printers.add_agent')}
      onClose={onClose}
      footer={
        made ? (
          <Button variant="primary" icon="check" onClick={onClose}>
            {t('printers.agent_done')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" icon="plus" onClick={add} busy={busy} disabled={!clean || isTaken}>
              {t('printers.add_agent')}
            </Button>
          </>
        )
      }
    >
      {!made ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <p className="muted">{t('printers.agent_intro')}</p>
          <Field label={t('printers.agent_name')} hint={t('printers.agent_name_hint')} error={isTaken ? t('printers.name_taken') : null}>
            <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-autofocus />
          </Field>
        </form>
      ) : (
        <div className="agent-key">
          <p className="callout callout-warn">
            <Icon name="lock" size={18} /> {t('printers.key_once')}
          </p>
          <ol className="agent-steps">
            <li>{t('printers.step_node')}</li>
            <li>
              {t('printers.step_file')}{' '}
              <a className="link" href="/downloads/print-agent.mjs" download>
                print-agent.mjs
              </a>
            </li>
            <li>
              {t('printers.step_run')}
              <div className="copy-box">
                <code className="mono">{command}</code>
                <IconButton icon="copy" label={t('printers.copy_command')} onClick={() => void copy(command)} />
              </div>
            </li>
            <li>{t('printers.step_choose')}</li>
          </ol>
        </div>
      )}
    </Dialog>
  );
}
